import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import PhotoCheck, { usePhotoNeeded } from "@/components/candidate/PhotoCheck";
import { useTenantBrand, BrandMark, BrandBar, PoweredBy } from "@/components/candidate/CandidateBrand";
import CodingTimer, { useCountdown } from "@/components/candidate/CodingTimer";
import { useCodeAutosave, AutosaveBadge, readLocalDraft, newestDraft } from "@/components/candidate/CodeAutosave";
import { usePasteGuard } from "@/components/candidate/PasteGuard";
import { CodeEditor } from "@/components/candidate/CodeEditor";
import { useRecordingParts } from "@/components/candidate/recordingParts";
import { ProctorRail } from "@/components/candidate/ProctorRail";
import { Play, Send, Sparkles, Loader2, Video as VideoIcon, AlertTriangle, Upload, Monitor } from "lucide-react";

export default function CodingRoom() {
  const { token } = useParams();
  const brand = useTenantBrand(token);
  const [photoNeeded, photoDone] = usePhotoNeeded(token, "coding");
  const nav = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [problem, setProblem] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [lang, setLang] = useState("python");
  const [code, setCode] = useState("");
  const [output, setOutput] = useState({ stdout: "", stderr: "" });
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ pct: 0, loaded: 0, total: 0 });
  const [uploadError, setUploadError] = useState(null);
  const [hint, setHint] = useState("");
  const [hintLoading, setHintLoading] = useState(false);
  const [proctor, setProctor] = useState({ tab_switches: 0, cam_live: false, screen_live: false, paste_events: 0, pasted_chars: 0, largest_paste: 0 });
  const selectionRef = useRef(null);
  const pasteGuard = usePasteGuard(setProctor, selectionRef);
  const [screenPrompt, setScreenPrompt] = useState(true);
  const [starting, setStarting] = useState(false);
  const [timeInfo, setTimeInfo] = useState({ limit: 45, remaining: null, started: false });
  const [deadline, setDeadline] = useState(null);
  const submittingRef = useRef(false);
  const autosave = useCodeAutosave(token, !!sessionId);
  const keystrokesRef = useRef([]);
  const runsRef = useRef([]);
  const chunksRef = useRef([]);
  const videoRef = useRef(null);          // candidate webcam preview
  const camStreamRef = useRef(null);      // getUserMedia stream (webcam + mic)
  const screenStreamRef = useRef(null);   // getDisplayMedia stream (screen video)
  const compositeStreamRef = useRef(null);// canvas.captureStream + mic track
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const recRef = useRef(null);
  const sessionIdRef = useRef(null);
  sessionIdRef.current = sessionId;
  const recParts = useRecordingParts("coding", () => sessionIdRef.current, chunksRef);

  // Load problem + languages immediately so the candidate sees the task.
  // Recording is NOT started until they click "Start recording" and grant
  // screen share — same pattern as HackerRank / CoderPad.
  useEffect(() => {
    (async () => {
      try {
        const [{ data: langs }, { data }] = await Promise.all([
          api.get("/coding/languages"),
          api.post(`/coding/${token}/start`),
        ]);
        if (data.already_submitted) { nav(`/invite/${token}/done`); return; }
        const allowed = data.languages?.length ? langs.filter(l => data.languages.includes(l.language)) : langs;
        const draft = newestDraft(data.draft, readLocalDraft(token));
        const restoreLang = draft && allowed.some(l => l.language === draft.language) && draft.drafts?.[draft.language] != null ? draft.language : null;
        const first = restoreLang || allowed[0]?.language || "python";
        setLanguages(allowed);
        setSessionId(data.session_id);
        setProblem(data.problem);
        setLang(first);
        setTimeInfo({ limit: data.time_limit_min || 45, remaining: data.remaining_sec, started: !!data.timer_started });
        if (restoreLang) {
          autosave.seed(draft);
          setCode(draft.drafts[restoreLang]);
          toast.success(`Restored your saved code from ${new Date(draft.saved_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`, { id: "code-restored" });
        } else {
          setCode(data.problem.starter?.[first] || "# your solution\n");
        }
      } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    })();
    const onVis = () => { if (document.hidden) setProctor(p => ({ ...p, tab_switches: p.tab_switches + 1 })); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      try { cancelAnimationFrame(rafRef.current); } catch(_) {}
      try { recRef.current?.stop(); } catch(_) {}
      try { camStreamRef.current?.getTracks().forEach(t => t.stop()); } catch(_) {}
      try { screenStreamRef.current?.getTracks().forEach(t => t.stop()); } catch(_) {}
    };
  }, [token]);

  // Compose: full-canvas screen capture + bottom-right picture-in-picture
  // webcam overlay. Recorded at 15fps which is plenty for a coding review
  // (editor content isn't changing fast) and keeps file size well under
  // the ingress limit for a 30-min session.
  const startRecording = async () => {
    if (starting) return;
    setStarting(true);
    try {
      // 1. Screen capture FIRST — Safari requires it directly in the click gesture.
      let screen = null;
      try {
        screen = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: 15 },
          audio: false,
        });
        screenStreamRef.current = screen;
        setProctor(p => ({ ...p, screen_live: true }));
        screen.getVideoTracks()[0].addEventListener("ended", () => {
          setProctor(p => ({ ...p, screen_live: false }));
        });
      } catch (_) {
        toast.error("Screen share declined — recording your webcam only. For full proctoring, please allow screen share.");
      }

      // 2. Webcam + mic
      let cam;
      try {
        cam = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch (e) {
        screen?.getTracks().forEach(t => t.stop());
        screenStreamRef.current = null;
        setProctor(p => ({ ...p, screen_live: false }));
        throw e;
      }
      camStreamRef.current = cam;
      if (videoRef.current) videoRef.current.srcObject = cam;
      setProctor(p => ({ ...p, cam_live: true }));

      // 3. Compose on canvas. Dimensions picked to match typical screen
      //    aspect ratio while keeping bitrate predictable.
      const CANVAS_W = 1280, CANVAS_H = 720;
      const canvas = document.createElement("canvas");
      canvas.width = CANVAS_W; canvas.height = CANVAS_H;
      canvasRef.current = canvas;
      const ctx = canvas.getContext("2d");

      // Hidden <video> elements fed by the two streams. We draw their
      // current frame to the canvas each tick.
      const screenEl = document.createElement("video");
      const camEl = document.createElement("video");
      screenEl.muted = camEl.muted = true;
      screenEl.playsInline = camEl.playsInline = true;
      if (screen) { screenEl.srcObject = screen; await screenEl.play().catch(() => {}); }
      camEl.srcObject = cam; await camEl.play().catch(() => {});

      // PiP geometry — 320x180 bottom-right with a 24px margin.
      const PIP_W = 320, PIP_H = 180, PIP_MARGIN = 24;
      const pipX = CANVAS_W - PIP_W - PIP_MARGIN;
      const pipY = CANVAS_H - PIP_H - PIP_MARGIN;

      const draw = () => {
        ctx.fillStyle = "#0A0D14";
        ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
        try {
          if (screen && screenEl.readyState >= 2) {
            // Fit screen into canvas preserving aspect ratio (letterbox).
            const sw = screenEl.videoWidth, sh = screenEl.videoHeight;
            if (sw && sh) {
              const scale = Math.min(CANVAS_W / sw, CANVAS_H / sh);
              const dw = sw * scale, dh = sh * scale;
              const dx = (CANVAS_W - dw) / 2, dy = (CANVAS_H - dh) / 2;
              ctx.drawImage(screenEl, dx, dy, dw, dh);
            }
          } else {
            // No screen capture → center the webcam full-canvas.
            if (camEl.readyState >= 2) {
              ctx.drawImage(camEl, 0, 0, CANVAS_W, CANVAS_H);
            }
          }
          if (screen && camEl.readyState >= 2) {
            // PiP webcam overlay with a thin red border so the recruiter
            // can audit expression + verify it's the same person who signed in.
            ctx.save();
            ctx.strokeStyle = "#ef4444";
            ctx.lineWidth = 3;
            ctx.strokeRect(pipX - 1.5, pipY - 1.5, PIP_W + 3, PIP_H + 3);
            ctx.drawImage(camEl, pipX, pipY, PIP_W, PIP_H);
            ctx.restore();
          }
        } catch(_) {}
        rafRef.current = requestAnimationFrame(draw);
      };
      draw();

      // 4. Build the composite MediaStream: canvas video track + mic audio
      //    track from the webcam stream.
      const canvasStream = canvas.captureStream(15);
      const micTrack = cam.getAudioTracks()[0];
      if (micTrack) canvasStream.addTrack(micTrack);
      compositeStreamRef.current = canvasStream;

      // 5. Record the composite with the same reliable settings as video.
      const RECORDER_OPTS = { videoBitsPerSecond: 500_000, audioBitsPerSecond: 48_000 };
      let mr;
      try {
        mr = new MediaRecorder(canvasStream, { mimeType: "video/webm;codecs=vp9,opus", ...RECORDER_OPTS });
      } catch(_) {
        mr = new MediaRecorder(canvasStream, RECORDER_OPTS);
      }
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start(1000);
      recRef.current = mr;
      recParts.start();
      setScreenPrompt(false);
      try {
        const { data: t } = await api.post(`/coding/${token}/timer/start`);
        setDeadline(Date.now() + t.remaining_sec * 1000);
      } catch (_) {
        setDeadline(Date.now() + timeInfo.limit * 60 * 1000);
      }
    } catch (e) {
      toast.error("Please enable your camera — it's a required proctor signal during coding.");
    } finally {
      setStarting(false);
    }
  };

  const onCode = (v) => {
    setCode(v);
    autosave.record(lang, v);
    keystrokesRef.current.push({ t: Date.now(), len: v.length });
  };

  const changeLang = (v) => {
    setLang(v);
    const next = autosave.drafts.current[v] ?? (problem?.starter?.[v] || `# ${v} starter\n`);
    setCode(next);
    autosave.record(v, next);
  };

  const runCode = async () => {
    setRunning(true); setOutput({stdout: "", stderr: ""});
    try {
      const langMeta = languages.find(l => l.language === lang);
      const { data } = await api.post("/coding/execute", { language: lang, version: langMeta?.version || "*", code });
      setOutput({ stdout: data.stdout || "", stderr: data.stderr || "" });
      runsRef.current.push({ t: Date.now(), stdout: (data.stdout||"").slice(0,500), stderr: (data.stderr||"").slice(0,500) });
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const askHint = async () => {
    setHintLoading(true);
    try {
      const { data } = await api.post("/coding/hint", { problem, code });
      setHint(data.hint);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setHintLoading(false); }
  };

  // XHR upload with progress + error check — mirrors VideoRoom's upload path
  // so coding recordings are as reliable as the video ones (same bitrate cap,
  // same 5-min timeout, same retry path). We DO NOT discard chunksRef on
  // failure so a Retry button can re-upload without forcing a re-record.
  const uploadRecording = async () => {
    if (recRef.current && recRef.current.state !== "inactive") {
      await new Promise((res) => { recRef.current.onstop = () => res(); try { recRef.current.stop(); } catch { res(); } });
    }
    try { cancelAnimationFrame(rafRef.current); } catch(_) {}
    const blob = new Blob(chunksRef.current, { type: "video/webm" });
    if (blob.size === 0) return { ok: true, bytes: 0 };
    // Preferred: most of the recording is already stored as 20s parts — send the tail and let the server join them.
    if (await recParts.finish(blob.size, setUploadProgress)) return { ok: true, bytes: blob.size };
    // Retry transient failures (dropped connection, timeout, 5xx) quietly before asking the candidate.
    let res;
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt) await new Promise((r) => setTimeout(r, attempt * 2000));
      res = await uploadOnce(blob);
      if (res.ok || !res.retryable) break;
    }
    return res;
  };

  const uploadOnce = (blob) => {
    const fd = new FormData();
    fd.append("file", blob, `coding-${sessionId}.webm`);
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API}/recordings/upload/coding/${sessionId}`, true);
      xhr.timeout = 5 * 60 * 1000;
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) {
          setUploadProgress({
            pct: Math.round((ev.loaded / ev.total) * 100),
            loaded: ev.loaded,
            total: ev.total,
          });
        }
      };
      xhr.onload = () => {
        if ((xhr.status >= 200 && xhr.status < 300) || xhr.status === 409) resolve({ ok: true, bytes: blob.size });
        else resolve({ ok: false, retryable: xhr.status >= 500, error: `upload ${xhr.status}: ${(xhr.responseText || "").slice(0, 200)}` });
      };
      xhr.onerror = () => resolve({ ok: false, retryable: true, error: "network error — upload connection was interrupted" });
      xhr.ontimeout = () => resolve({ ok: false, retryable: true, error: "upload timed out after 5 minutes" });
      try { xhr.send(fd); }
      catch (e) { resolve({ ok: false, error: e?.message || String(e) }); }
    });
  };

  const submit = async (auto = false) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    if (auto) toast.warning("Time's up — submitting your code automatically");
    try {
      await api.post("/coding/submit", { session_id: sessionId, code, language: lang, keystrokes: keystrokesRef.current.slice(-500), runs: runsRef.current, proctor_flags: proctor, auto_submitted: auto });
      // Upload proctor video — now with status check + progress indicator.
      // A failure here surfaces the real error and offers Retry/Skip so
      // the candidate doesn't silently lose the recording.
      setUploading(true);
      setUploadError(null);
      setUploadProgress({ pct: 0, loaded: 0, total: 0 });
      const result = await uploadRecording();
      if (!result.ok) {
        setUploadError(result.error || "Upload failed");
        toast.success("Code submitted — recording upload hit an issue");
        return; // stay on page; Retry button is rendered in the uploading overlay
      }
      try { camStreamRef.current?.getTracks().forEach(t => t.stop()); } catch(_) {}
      try { screenStreamRef.current?.getTracks().forEach(t => t.stop()); } catch(_) {}
      toast.success("Submitted");
      setUploading(false);
      nav(`/invite/${token}/done`);
    } catch (e) {
      if (e.response?.status === 409) { toast.error(fmtErr(e.response.data?.detail)); nav(`/invite/${token}/done`); return; }
      submittingRef.current = false;
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };
  const timeLeft = useCountdown(deadline, () => submit(true));

  const retryUpload = async () => {
    setUploadError(null);
    setUploadProgress({ pct: 0, loaded: 0, total: 0 });
    const result = await uploadRecording();
    if (!result.ok) { setUploadError(result.error || "Upload failed"); return; }
    try { camStreamRef.current?.getTracks().forEach(t => t.stop()); } catch(_) {}
    try { screenStreamRef.current?.getTracks().forEach(t => t.stop()); } catch(_) {}
    toast.success("Recording uploaded");
    setUploading(false);
    nav(`/invite/${token}/done`);
  };

  const skipUpload = () => {
    try { camStreamRef.current?.getTracks().forEach(t => t.stop()); } catch(_) {}
    try { screenStreamRef.current?.getTracks().forEach(t => t.stop()); } catch(_) {}
    toast.error("Continuing without the proctor recording.");
    setUploading(false);
    nav(`/invite/${token}/done`);
  };

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden flex flex-col" data-testid="coding-room" style={{background: "#0A0D14"}}>
      {screenPrompt && photoNeeded && <PhotoCheck token={token} source="coding" onDone={photoDone} />}
      {screenPrompt && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-sm" data-testid="coding-screen-prompt">
          <div className="tp-card p-8 max-w-md w-full">
            <div className="w-12 h-12 rounded-full bg-indigo-500/15 border border-indigo-400/40 grid place-items-center mx-auto mb-4">
              <Monitor className="w-6 h-6 text-indigo-300"/>
            </div>
            <div className="font-display text-lg font-semibold mb-2 text-center">Start Coding Interview</div>
            <div className="text-sm text-slate-300 leading-relaxed mb-4">
              For an audit-grade coding review, we record your <strong>screen</strong> and <strong>webcam</strong> together — like being in the same room as the interviewer. You'll see one browser prompt to share your screen; please choose <strong>Entire Screen</strong> for best coverage.
            </div>
            <div data-testid="coding-time-notice" className="text-sm font-medium text-indigo-200 bg-indigo-500/10 border border-indigo-500/30 rounded-lg px-3 py-2 mb-4">
              {timeInfo.started
                ? <>Your timer is already running — <b>{Math.ceil((timeInfo.remaining || 0) / 60)} min</b> left.</>
                : <>You'll have <b>{timeInfo.limit} minutes</b>. The timer starts when you click Start, and your code is submitted automatically when time runs out.</>}
            </div>
            <ul className="text-xs text-slate-400 space-y-1 mb-5 list-disc pl-5">
              <li>Your code editor + any tab you open is captured</li>
              <li>Your webcam appears as a small overlay in the recording</li>
              <li>Your recording is uploaded when you submit</li>
            </ul>
            <button
              data-testid="coding-start-recording"
              onClick={startRecording}
              disabled={starting || photoNeeded !== false}
              className="w-full inline-flex items-center justify-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium disabled:opacity-60"
            >
              {starting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Monitor className="w-4 h-4"/>}
              {starting ? "Starting…" : "Start recording & begin"}
            </button>
          </div>
        </div>
      )}
      {uploading && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm" data-testid="coding-upload-overlay">
          <div className="tp-card p-8 max-w-md w-full text-center">
            {uploadError ? (
              <>
                <div className="w-12 h-12 rounded-full bg-rose-500/15 border border-rose-400/40 grid place-items-center mx-auto mb-4">
                  <AlertTriangle className="w-6 h-6 text-rose-300"/>
                </div>
                <div className="font-display text-lg font-semibold mb-2">Recording upload failed</div>
                <div className="text-sm text-slate-400 mb-4" data-testid="coding-upload-error">{uploadError}</div>
                <div className="text-xs text-slate-500 mb-4">Your code is already submitted. You can retry the proctor-video upload (same recording, no need to redo) or continue without it.</div>
                <div className="flex gap-2 justify-center">
                  <button data-testid="coding-retry-upload" onClick={retryUpload} className="text-sm px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium">Retry upload</button>
                  <button data-testid="coding-skip-upload" onClick={skipUpload} className="text-sm px-4 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5">Continue without</button>
                </div>
              </>
            ) : (
              <>
                <Loader2 className="w-10 h-10 text-indigo-400 mx-auto mb-4 animate-spin"/>
                <div className="font-display text-lg font-semibold mb-2">Uploading proctor recording…</div>
                {uploadProgress.total > 0 ? (
                  <>
                    <div className="text-sm text-slate-200 mb-2" data-testid="coding-upload-progress">
                      {(uploadProgress.loaded / (1024*1024)).toFixed(1)} MB of {(uploadProgress.total / (1024*1024)).toFixed(1)} MB uploaded ({uploadProgress.pct}%)
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-4">
                      <div className="h-full bg-indigo-500 transition-all duration-200" style={{ width: `${uploadProgress.pct}%` }}/>
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-slate-400 mb-4">Preparing recording for upload…</div>
                )}
                <div className="text-xs text-slate-500">Please keep this tab open — your interview will complete automatically.</div>
              </>
            )}
          </div>
        </div>
      )}
      <BrandBar tenant={brand} label="Live Coding" />
      <header className="border-b border-[color:var(--bd-sub)] px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wider text-slate-500">Coding Sandbox {proctor.cam_live && <span className="tp-chip tp-chip-rose ml-3"><span className="tp-rec-pulse mr-1"/>CAM</span>} {proctor.screen_live && <span className="tp-chip tp-chip-rose ml-2"><span className="tp-rec-pulse mr-1"/>SCREEN</span>} {proctor.tab_switches > 0 && <span className="tp-chip tp-chip-amber ml-2"><AlertTriangle className="w-3 h-3 mr-1"/>tabs: {proctor.tab_switches}</span>}</div>
        <div className="flex flex-wrap items-center gap-2">
          <AutosaveBadge status={autosave.status} />
          <CodingTimer left={timeLeft} limitMin={timeInfo.limit} />
          <select data-testid="lang-select" value={lang} onChange={(e)=>changeLang(e.target.value)} className="text-xs bg-[color:var(--bg-card)] border border-[color:var(--bd-sub)] rounded-md px-2 py-1.5">
            {languages.map(l => <option key={l.language} value={l.language}>{l.label}</option>)}
          </select>
          <button data-testid="hint-btn" onClick={askHint} disabled={hintLoading} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[color:var(--bd-hi)] hover:bg-white/5">
            {hintLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Sparkles className="w-3.5 h-3.5"/>} Hint
          </button>
          <button data-testid="run-btn" onClick={runCode} disabled={running} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Play className="w-3.5 h-3.5"/>} Run
          </button>
          <button data-testid="submit-btn" onClick={() => submit(false)} disabled={submitting || uploading || screenPrompt} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-60">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : uploading ? <Upload className="w-3.5 h-3.5"/> : <Send className="w-3.5 h-3.5"/>} {uploading ? "Uploading…" : "Submit"}
          </button>
        </div>
      </header>

      <div className="relative flex-1 grid grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)_232px] lg:grid-rows-[minmax(0,1fr)] min-h-0">
        <aside className="border-r border-[color:var(--bd-sub)] p-5 overflow-y-auto min-h-0 max-h-[45vh] lg:max-h-none">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Problem</div>
          <div className="font-display text-xl font-semibold mb-3">{problem?.title || "Loading…"}</div>
          <div className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap mb-4">{problem?.description}</div>
          {problem?.examples?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Examples</div>
              {problem.examples.map((ex, i) => (
                <div key={i} className="font-mono text-xs bg-[color:var(--bg-void)] border border-[color:var(--bd-sub)] rounded-md p-3 mb-2">
                  <div><span className="text-slate-500">in: </span>{ex.input}</div>
                  <div><span className="text-slate-500">out: </span>{ex.output}</div>
                </div>
              ))}
            </div>
          )}
          {hint && (
            <div className="mt-4 p-3 rounded-md bg-indigo-500/10 border border-indigo-500/30 text-xs text-indigo-100 leading-relaxed">
              <div className="text-indigo-300 uppercase tracking-wider text-[10px] mb-1 font-mono">Co-pilot hint</div>
              {hint}
            </div>
          )}
        </aside>

        <section className="flex flex-col min-h-0 relative">
          <CodeEditor value={code} language={lang} onChange={onCode} onRun={runCode} pasteGuard={pasteGuard} selectionRef={selectionRef} />
          <div className="relative border-t border-[color:var(--bd-sub)] bg-[color:var(--bg-void)]" style={{ background: "#04060A" }}>
            <div className="px-5 py-2 text-xs uppercase tracking-wider text-slate-500 flex items-center justify-between">
              <span>Console</span>
              <span className="text-slate-600">{runsRef.current.length} runs · {keystrokesRef.current.length} keystrokes captured</span>
            </div>
            <pre className="px-5 pb-5 font-mono text-xs text-slate-200 whitespace-pre-wrap min-h-[100px] max-h-[240px] overflow-y-auto" data-testid="code-output">
              {output.stdout && <span className="text-emerald-300">{output.stdout}</span>}
              {output.stderr && <span className="text-rose-300">{output.stderr}</span>}
              {!output.stdout && !output.stderr && <span className="text-slate-600">Run your code to see output here.</span>}
            </pre>
          </div>
        </section>
        <ProctorRail videoRef={videoRef} proctor={proctor} />
      </div>
      <PoweredBy />
    </div>
  );
}
